import {
  HOSPITALIZATION_ROWS,
  LAST_PCP_ROWS,
  SOCIAL_HISTORY_GROUPS,
  SPECIALIST_OPTIONS,
} from "@/pages/app/provider/patient/patient-list/patient-chart-new/patient-visit-note/encounter/note-forms/readmission-f2f";
import { parseCodes, parseText } from "./initial-f2f-note-sections";
import {
  INITIAL_F2F_OPTION_SECTIONS,
  NOTE_CONTENT_OPTION_SECTIONS,
  NOTE_CONTENT_SECTION_KEYS,
  NOTE_CPT_CODES_ANSWER_KEY,
  NOTE_CPT_FREE_TEXT_ANSWER_KEY,
  NOTE_DIAGNOSIS_CODES_ANSWER_KEY,
  NOTE_DIAGNOSIS_FREE_TEXT_ANSWER_KEY,
  NOTE_SCREENING_CUSTOM_ANSWER_KEY,
  NOTE_SCREENING_FREE_TEXT_ANSWER_KEY,
  NOTE_HISTORY_FREE_TEXT_SECTIONS,
  NOTE_PHARMACIES_ANSWER_KEY,
} from "./initial-f2f-constant";
import {
  Answers,
  getAnswerKey,
  parseFreeText,
  parseGroupAnswers,
  parseRowAnswers,
} from "./initial-f2f-note-answers";
import { NoteContentFields, NoteSections } from "./initial-f2f-types";
import { parseVitalsSection } from "./initial-f2f-vitals";
import { parseMiniCogAnswers } from "./initial-f2f-mini-cog-utils";

const HOSPITALIZATION_LABELS = HOSPITALIZATION_ROWS.map((row) => row.key);
const LAST_PCP_LABELS = LAST_PCP_ROWS.map((row) => row.key);

/** Inverse of `buildNoteContent`; every synced field is returned so a missing section resets to empty. */
export const parseNoteContent = (
  noteContent: Record<string, object> | undefined
): NoteContentFields => {
  const sections = (noteContent?.sections ?? {}) as NoteSections;
  const answersOf = (key: string): Answers => sections[key]?.answers ?? {};
  const keys = NOTE_CONTENT_SECTION_KEYS;
  const planAnswers = answersOf(keys.planOfCare);
  const diagnosisAnswers = answersOf(keys.diagnosis);

  return {
    historyOfPresentIllness: sections[keys.hpi]?.text ?? "",
    hospitalization: parseRowAnswers(
      HOSPITALIZATION_LABELS,
      answersOf(keys.hospitalization)
    ),
    hospitalizationFreeText: parseFreeText(answersOf(keys.hospitalization)),
    socialHistory: parseGroupAnswers(
      SOCIAL_HISTORY_GROUPS,
      answersOf(keys.socialHistory)
    ),
    lastPcpVisitAndLabs: parseRowAnswers(
      LAST_PCP_LABELS,
      answersOf(keys.lastPcp)
    ),
    lastPcpVisitFreeText: parseFreeText(answersOf(keys.lastPcp)),
    currentMedicalSpecialist: parseRowAnswers(
      SPECIALIST_OPTIONS,
      answersOf(keys.specialist)
    ),
    currentMedicalSpecialistFreeText: parseFreeText(answersOf(keys.specialist)),
    sectionFreeText: Object.fromEntries(
      NOTE_HISTORY_FREE_TEXT_SECTIONS.map((key) => [
        key,
        parseFreeText(answersOf(keys[key])),
      ])
    ),
    allergyFreeText: parseFreeText(answersOf(keys.allergy)),
    medicationFreeText: parseFreeText(answersOf(keys.medication)),
    pharmacies:
      answersOf(keys.pharmacy)[NOTE_PHARMACIES_ANSWER_KEY]?.pharmacies ?? [],
    sectionOptions: Object.fromEntries(
      NOTE_CONTENT_OPTION_SECTIONS.map((key) => [
        key,
        parseGroupAnswers(
          INITIAL_F2F_OPTION_SECTIONS[key].groups,
          answersOf(keys[key])
        ),
      ])
    ),
    sectionDates: Object.fromEntries(
      NOTE_CONTENT_OPTION_SECTIONS.flatMap((key) => {
        const dateGroup = INITIAL_F2F_OPTION_SECTIONS[key].dateGroup;
        if (!dateGroup) return [];
        const answers = answersOf(keys[key]);
        const dates = Object.fromEntries(
          dateGroup.fields.flatMap(({ key: dateKey }) => {
            const value = answers[getAnswerKey(dateKey)]?.value;
            return typeof value === "string" ? [[dateKey, value]] : [];
          })
        );
        return [[key, dates]];
      })
    ),
    diagnosisCodes: parseCodes(
      diagnosisAnswers,
      NOTE_DIAGNOSIS_CODES_ANSWER_KEY
    ),
    diagnosisFreeText: parseText(
      diagnosisAnswers,
      NOTE_DIAGNOSIS_FREE_TEXT_ANSWER_KEY
    ),
    screeningCustom: parseCodes(
      answersOf(keys.screening),
      NOTE_SCREENING_CUSTOM_ANSWER_KEY
    ),
    screeningFreeText: parseText(
      answersOf(keys.screening),
      NOTE_SCREENING_FREE_TEXT_ANSWER_KEY
    ),
    miniCog: parseMiniCogAnswers(answersOf(keys.miniCog)),
    ...parseVitalsSection(answersOf(keys.vitalSigns)),
    cptCodes: parseCodes(planAnswers, NOTE_CPT_CODES_ANSWER_KEY),
    cptFreeText: parseText(planAnswers, NOTE_CPT_FREE_TEXT_ANSWER_KEY),
  };
};
